<template>
  <div class="mizona-container">
    <div class="content">
      <!-- 🔥 SKELETON -->
      <div v-if="isLoading">
        <div class="profile-section">
          <div class="avatar skeleton"></div>
          <div class="skeleton skeleton-text title"></div>
          <div class="skeleton skeleton-text subtitle"></div>
        </div>

        <div class="menu-list">
          <div v-for="i in 8" :key="i" class="menu-item">
            <div class="icon skeleton"></div>
            <div class="skeleton skeleton-text line"></div>
          </div>
        </div>
      </div>

      <!-- ✅ CONTENIDO REAL -->
      <div v-else>
        <!-- PERFIL -->
        <div class="profile-section">
          <div class="avatar-wrapper" @click="selectImage">
            <img :src="getAvatarUrl(user?.avatar)" class="avatar" />
            <div v-if="isUploading" class="avatar-loader">
              <div class="spinner"></div>
            </div>
            <div class="edit-avatar">
              <span class="material-icons">edit</span>
            </div>
          </div>
          <!-- input oculto -->
          <input
            type="file"
            ref="fileInput"
            accept="image/*"
            style="display: none"
            @change="uploadAvatar"
          />

          <h2 class="welcome">Hola! {{ user?.first_name }}</h2>
          <p class="email">{{ user?.email }}</p>

          <a class="edit-profile" @click="handleEditProfile"> Editar perfil </a>
        </div>

        <!-- MENÚ -->
        <div class="menu-list">
          <div class="menu-item" @click="goTo('/misuscripcion')">
            <span class="material-icons">workspace_premium</span>
            <span>Mi Suscripción</span>
            <span class="material-icons arrow">chevron_right</span>
          </div>

          <div class="menu-item" @click="goTo('/favoritos')">
            <span class="material-icons">favorite</span>
            <span>Mis Favoritos</span>
            <span class="material-icons arrow">chevron_right</span>
          </div>

          <div class="menu-item" @click="goTo('/newsletters')">
            <span class="material-icons">mail</span>
            <span>Newsletters</span>
            <span class="material-icons arrow">chevron_right</span>
          </div>

          <div class="menu-item">
            <span class="material-icons">notifications</span>
            <span>Notificaciones</span>
            <span class="material-icons arrow">chevron_right</span>
          </div>

          <div class="menu-item" @click="goTo('/avisolegal')">
            <span class="material-icons">gavel</span>
            <span>Aviso Legal</span>
            <span class="material-icons arrow">chevron_right</span>
          </div>

          <div class="menu-item" @click="goTo('/policy')">
            <span class="material-icons">privacy_tip</span>
            <span>Política de privacidad</span>
            <span class="material-icons arrow">chevron_right</span>
          </div>

          <div class="menu-item" @click="goTo('/quienes-somos')">
            <span class="material-icons">info</span>
            <span>¿Quienes somos?</span>
            <span class="material-icons arrow">chevron_right</span>
          </div>

          <div class="menu-item" @click="goTo('/equipo-editorial')">
            <span class="material-icons">people</span>
            <span>Equipo Editoria</span>
            <span class="material-icons arrow">chevron_right</span>
          </div>

          <div class="menu-item" @click="goTo('/anunciaconnosotros')">
            <span class="material-icons">campaign</span>
            <span>Anuncia con Nosotros</span>
            <span class="material-icons arrow">chevron_right</span>
          </div>

          <div class="menu-item" @click="contactEmail">
            <span class="material-icons">contact_mail</span>
            <span>Contacta con Nosotros</span>
            <span class="material-icons arrow">chevron_right</span>
          </div>

          <div class="menu-item logout" @click="logout">
            <span class="material-icons">logout</span>
            <span>Cerrar Sesión</span>
          </div>
        </div>
      </div>
    </div>

    <!-- FOOTER -->
    <div class="footer">
      <p>JLHVISION versión 1.0</p>
      <p>© 2026 JLHVISION. Todos los derechos reservados.</p>
      <p>Desarrollado por <strong>JLHSOFT</strong></p>
    </div>

    <!-- MODAL PREMIUM -->
    <div v-if="showPremiumModal" class="modal-overlay">
      <div class="modal">
        <div class="modal-icon">🔒</div>

        <h2>Acceso restringido</h2>
        <p>{{ modalMessage }}</p>

        <button class="modal-btn" @click="showPremiumModal = false">
          Aceptar
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import api from "../../services/api";
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();
const user = ref(null);
const isLoading = ref(true);
const fileInput = ref(null);
const isUploading = ref(false);
const showPremiumModal = ref(false);
const modalMessage = ref("");
const siteEmail = ref("");

onMounted(() => {
  loadSettings();
  setTimeout(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      user.value = JSON.parse(userData);
    }
    isLoading.value = false;
  }, 800); // simula carga
});

// Cargar settings
const loadSettings = async () => {
  try {
    const response = await api.get("/settings");
    siteEmail.value = response.data.setting.site_email;
  } catch (error) {
    console.error("Error cargando settings:", error);
  }
};

const contactEmail = () => {
  if (!siteEmail.value) return;

  window.location.href = `mailto:${siteEmail.value}`;
};

const checkPremium = () => {
  return user.value?.is_premium === true;
};

const requirePremium = (message) => {
  if (!checkPremium()) {
    modalMessage.value = message;
    showPremiumModal.value = true;
    return false;
  }
  return true;
};

const selectImage = () => {
  if (!requirePremium("Debes tener una suscripción para cambiar tu avatar"))
    return;

  fileInput.value.click();
};

const handleEditProfile = () => {
  if (!requirePremium("Debes tener una suscripción para editar tus datos"))
    return;
  router.push("/editar-perfil");
};

const uploadAvatar = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const formData = new FormData();
  formData.append("avatar", file);

  isUploading.value = true; // 🔥 iniciar loader

  try {
    const response = await api.post("/app/profile/changeavatar", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    if (response.data.user) {
      user.value = response.data.user;
      localStorage.setItem("user", JSON.stringify(response.data.user));

      window.dispatchEvent(new Event("user-updated"));
    }
  } catch (error) {
    console.error("Error subiendo avatar:", error);
  } finally {
    isUploading.value = false;
  }
};

const getAvatarUrl = (avatar) => {
  if (!avatar) return "/default-avatar.png";
  if (avatar.startsWith("http")) return avatar;
  return `https://portal.jlhvision.com/avatars/${avatar}`;
};

const goTo = (path) => {
  if (path === "/favoritos") {
    if (!requirePremium("Debes tener una suscripción para ver tus favoritos"))
      return;
  }

  if (path === "/newsletters") {
    if (
      !requirePremium("Debes tener una suscripción para acceder a newsletters")
    )
      return;
  }

  router.push(path);
};

const logout = () => {
  localStorage.removeItem("auth_token");
  localStorage.removeItem("user");
  router.push("/login");
};
</script>

<style scoped>
.mizona-container {
  margin-top: 45px;
  background: #fff;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.content {
  flex: 1;
  padding: 20px;
}

/* PERFIL */
.profile-section {
  text-align: center;
  margin-bottom: 30px;
}

.avatar-wrapper {
  position: relative;
  width: 90px;
  height: 90px;
  margin: auto;
}

.avatar {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  object-fit: cover;
  margin: auto;
}

.edit-avatar {
  position: absolute;
  bottom: 0;
  right: 0;
  background: #a50000;
  border-radius: 50%;
  padding: 6px;
}

.edit-avatar .material-icons {
  color: #fff;
  font-size: 16px;
}

/* overlay loader */
.avatar-loader {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* spinner pequeño */
.spinner {
  width: 22px;
  height: 22px;
  border: 2px solid #ddd;
  border-top-color: #a50000;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

/* ocultar input */
.file-input {
  display: none;
}

.welcome {
  margin-top: 10px;
  font-size: 18px;
}

.email {
  color: #777;
  font-size: 14px;
}

.edit-profile {
  color: #a50000;
  font-size: 13px;
  cursor: pointer;
  text-decoration: none;
}

/* MENU */
.menu-list {
  margin-top: 10px;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 15px 0;
  gap: 12px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.menu-item:hover {
  background: #fafafa;
}

.menu-item .material-icons {
  color: #555;
}

.menu-item .arrow {
  margin-left: auto;
}

.menu-item.logout {
  color: #a50000;
}

/* FOOTER */
.footer {
  text-align: center;
  padding: 40px 10px;
  font-size: 12px;
  color: #999;
}

/* 🔥 SKELETON */
.skeleton {
  background: linear-gradient(90deg, #eee, #f5f5f5, #eee);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

.skeleton-text {
  height: 12px;
  margin: 8px auto;
}

.title {
  width: 120px;
  height: 16px;
}

.subtitle {
  width: 180px;
}

.line {
  width: 100%;
}

.icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* =========================
   MODAL PREMIUM
========================= */

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  padding: 20px;
}

.modal {
  background: #ffffff;
  padding: 25px;
  border-radius: 14px;
  text-align: center;
  width: 300px;
  animation: fadeIn 0.25s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

/* icono */
.modal-icon {
  font-size: 40px;
  margin-bottom: 10px;
  color: #ff9800;
}

/* título */
.modal h2 {
  margin: 0;
  font-size: 18px;
  color: #00144a;
}

/* texto */
.modal p {
  font-size: 14px;
  color: #666;
  margin: 10px 0 20px;
  line-height: 1.4;
}

/* botón */
.modal-btn {
  background: #00144a;
  color: white;
  border: none;
  padding: 10px 18px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: 0.2s;
}

.modal-btn:hover {
  background: #002a73;
}

/* animación */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.92);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
